import React, { useRef, useState } from "react";
import { AuthLetterMismatch } from "../../../../data-model/cptCodesTableModel";
import WarningIcon from "../../../../svg/warningIcon";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";

/** Muted red for the auth-letter mismatch badge and tooltip accent. */
const MISMATCH_RED = "#B42318";

/**
 * Renders a red count badge for auth-letter field mismatches. On hover, shows a
 * portal tooltip listing each CPT-table field that differs from the
 * Authorization Details screen alongside the agent-extracted value.
 *
 * @input mismatchFields - Mismatched fields with the agent value to display
 * @return JSX.Element | null: Count badge with tooltip, or null when empty
 */
const MismatchWarning: React.FC<{ mismatchFields?: AuthLetterMismatch[] }> = ({
  mismatchFields,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const fields = mismatchFields ?? [];

  if (fields.length === 0) {
    return null;
  }

  return (
    <div
      ref={anchorRef}
      className="absolute -right-1.5 -top-1.5 z-10 flex h-[14px] min-w-[14px] shrink-0 cursor-default items-center justify-center rounded-full px-[3px] text-[9px] font-semibold leading-none text-white ring-1 ring-white"
      style={{ backgroundColor: MISMATCH_RED }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-hidden="true"
    >
      {fields.length}
      <ToolTipUsingPortal
        placement="left"
        showTooltip={showTooltip}
        parentRef={anchorRef}
        wrapText
        maxWidth="350px"
      >
        <div className="flex w-max min-w-[280px] flex-col gap-2 p-0.5">
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <WarningIcon
              height="13"
              width="13"
              outerFillColor={MISMATCH_RED}
              innerFillColor="#FFFFFF"
            />
            <span className="font-semibold text-primaryGray-1">
              Auth letter has different values for:
            </span>
          </div>
          <div className="flex flex-col gap-1.5 border-t border-primaryGray-15 pt-1.5">
            {fields.map(({ field, agentValue }) => (
              <div
                key={field}
                className="flex items-baseline justify-between gap-6 whitespace-nowrap"
              >
                <span className="text-primaryGray-4">{field}</span>
                <span className="font-semibold text-primaryGray-1">
                  {agentValue}
                </span>
              </div>
            ))}
          </div>
        </div>
      </ToolTipUsingPortal>
    </div>
  );
};

export default MismatchWarning;
